(() => {
  class MedicalRecommendations extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.interacted = false;
      this.addEventListener('pointerdown', () => { this.interacted = true; }, { signal, once: true });
      this.addEventListener('focusin', () => { this.interacted = true; }, { signal, once: true });
      this.addEventListener('click', event => {
        const button = event.target.closest('[data-med-recs-prev],[data-med-recs-next]');
        if (!button || button.disabled || !this.track) return;
        this.interacted = true;
        const direction = button.hasAttribute('data-med-recs-prev') ? -1 : 1;
        const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
        this.track.scrollBy({ left: direction * rtl * this.track.clientWidth, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      }, { signal });
      this.bindTrack();
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.updateControls());
        this.resizeObserver.observe(this);
      }
      window.addEventListener('resize', () => this.updateControls(), { signal, passive: true });
      if (this.dataset.performed === 'true' || !this.dataset.productId) return;
      if (this.hidden || typeof IntersectionObserver !== 'function') {
        this.loadRecommendations();
      } else {
        this.observer = new IntersectionObserver(entries => {
          if (!entries.some(entry => entry.isIntersecting)) return;
          this.observer.disconnect();
          this.loadRecommendations();
        }, { rootMargin: '400px' });
        this.observer.observe(this);
      }
    }
    bindTrack() {
      this.track = this.querySelector('.med-recs-track');
      this.track?.addEventListener('scroll', () => this.updateControls(), { signal: this.controller.signal, passive: true });
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.updateControls());
    }
    updateControls() {
      if (!this.track || !this.isConnected) return;
      const controls = this.querySelector('.med-recs-controls');
      const previous = this.querySelector('[data-med-recs-prev]');
      const next = this.querySelector('[data-med-recs-next]');
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      const position = Math.abs(this.track.scrollLeft);
      if (controls) controls.hidden = maximum <= 2;
      if (previous) previous.disabled = position <= 2;
      if (next) next.disabled = position >= maximum - 2;
    }
    async loadRecommendations() {
      if (this.loading || !this.dataset.url || !this.dataset.productId) return;
      this.loading = true;
      this.fetchController = new AbortController();
      try {
        const url = new URL(this.dataset.url, location.origin);
        if (url.origin !== location.origin) return;
        url.searchParams.set('product_id', this.dataset.productId);
        url.searchParams.set('section_id', this.dataset.sectionId);
        url.searchParams.set('limit', String(Math.min(10, Math.max(4, Number(this.dataset.limit) || 6))));
        url.searchParams.set('intent', 'related');
        const response = await fetch(url, { signal: this.fetchController.signal });
        if (!response.ok) return;
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const incoming = html.getElementById(this.id);
        const track = incoming?.querySelector('.med-recs-track');
        if (!this.isConnected || this.interacted || !track?.querySelector('.med-recs-item')) return;
        this.track.replaceWith(track);
        this.hidden = false;
        this.dataset.performed = 'true';
        this.bindTrack();
      } catch {
        // Keep the server-rendered product cards if recommendations cannot load.
      } finally {
        this.loading = false;
      }
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.fetchController?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }

  // UI state only: preserve the sidebar position when Shopify refreshes results.
  const sidebarStates = new Map();
  class MedicalFilterSidebar extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      this.stateKey = `${location.pathname}:${this.dataset.sectionId}`;
      this.scroller = this.querySelector('.med-filter-scroll');
      if (!this.scroller) return;
      this.bindScroller(this.scroller);
      this.restore();
      const { signal } = this.controller;
      this.addEventListener('change', () => this.remember(), { signal, capture: true });
      this.addEventListener('toggle', () => this.remember(), { signal, capture: true });
      this.mutations = new MutationObserver(() => {
        this.restoreVersion = (this.restoreVersion || 0) + 1;
        this.restoring = true;
        cancelAnimationFrame(this.frame);
        this.frame = requestAnimationFrame(() => this.restore());
      });
      this.mutations.observe(this, { childList: true, subtree: true });
    }
    bindScroller(scroller) {
      this.scroller = scroller;
      scroller.addEventListener('scroll', () => this.remember(), { signal: this.controller.signal, passive: true });
    }
    remember() {
      if (!this.scroller || this.restoring) return;
      const open = {};
      this.querySelectorAll('.med-filter-group details[id]').forEach(detail => { open[detail.id] = detail.open; });
      sidebarStates.set(this.stateKey, { top: this.scroller.scrollTop, open });
    }
    restore() {
      const state = sidebarStates.get(this.stateKey);
      const scroller = this.querySelector('.med-filter-scroll');
      if (!scroller) { this.restoring = false; return; }
      if (scroller !== this.scroller) this.bindScroller(scroller);
      if (!state) { this.restoring = false; return; }
      const version = this.restoreVersion = (this.restoreVersion || 0) + 1;
      this.restoring = true;
      this.scroller = scroller;
      this.querySelectorAll('.med-filter-group details[id]').forEach(detail => {
        if (Object.hasOwn(state.open, detail.id)) detail.open = state.open[detail.id];
      });
      scroller.scrollTop = state.top;
      queueMicrotask(() => { if (version === this.restoreVersion) this.restoring = false; });
    }
    disconnectedCallback() {
      this.controller?.abort();
      this.mutations?.disconnect();
      cancelAnimationFrame(this.frame);
    }
  }
  if (!customElements.get('med-recommendations')) customElements.define('med-recommendations', MedicalRecommendations);
  if (!customElements.get('med-filter-sidebar')) customElements.define('med-filter-sidebar', MedicalFilterSidebar);
})();
